import React from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  ChevronLeftRounded as ChevronLeftIcon,
  ChevronRightRounded as ChevronRightIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import unofficialModeIndicatorGIF from "../../../assets/icons/unofficial-mode.gif";
import {
  BLUE,
  BLUE_DARK,
  BORDER,
  BORDER_MED,
  ERROR_BG,
  ERROR_C,
  INK,
  INK_MUTED,
  INK_SOFT,
  textFieldSx,
  navBtnSx,
} from "./shared";
import PawnNavButtons from "../pawnNavButtons";
import { CustomerTxnPopper } from "../customerTxnLookup";

// ─── Page header: title, voucher no, ticket nav + search, and the
// Loan-date / Save / Cancel / New action cluster, plus the read-only banner.
// Extracted verbatim from PaymentScreen; all state/handlers arrive as props.
const PageHeader = ({
  isMobile,
  editMode,
  pawnMode,
  manualVoucherMode,
  currentFormattedVoucherNo,
  handleManualVoucherChange,
  voucherCheck,
  voucherWarn,
  currentVoucherNo,
  navigateTicket,
  canGoForward,
  loadingTicket,
  ticketSearch,
  setTicketSearch,
  loadPawnForEdit,
  formData,
  setFormData,
  paymentLocked,
  // A Store Staff session looking at a payment already on file. Implies
  // paymentLocked; additionally the Save/Update button is not drawn at all.
  roleLocked,
  // The receipt-chain lock on its own, so the banner can say which lock it is.
  receiptLocked,
  loading,
  handleSave,
  voucherBlocked,
  handleCancel,
  handleNewPayment,
  loanSettled,
  // Mobile-number lookup on the same search box: type a customer's phone
  // number, pick them from the dropdown, load their last loan.
  customerLookup,
}) => {
  const manualVoucherFilled = !!String(currentFormattedVoucherNo ?? "").trim();
  // The chevrons walk tickets in creation order. Backward always has somewhere
  // to go (worst case: the most recent ticket), but forward only exists once a
  // ticket is actually loaded — on a fresh form nothing sits ahead of the entry
  // being typed, so the right chevron is not rendered at all.
  return (
    <>
      {/* ── Page Header ──────────────────────────────────────────────────── */}
      {/* Three clusters — title+voucher, the ticket nav and the
              action buttons — need roughly 1050px side by side, so the row only
              forms at lg. At md they used to overlap into each other. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "stretch", lg: "center" },
          gap: 1.5,
          mb: { xs: 2, md: 2.5 },
          pb: 2,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
            minWidth: 0,
            width: { xs: "100%", lg: "auto" },
          }}
        >
          <PawnNavButtons />
          <Box
            sx={{
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: "10px",
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
            }}
          >
            <AccountBalanceIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: isMobile ? "1.1rem" : "1.35rem",
                  color: INK,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                {roleLocked
                  ? "View Pawn Payment"
                  : editMode
                  ? "Edit Pawn Payment"
                  : "Pawn Payment"}
              </Typography>
            </Box>
            {manualVoucherMode ? (
              <TextField
                variant="standard"
                size="small"
                value={currentFormattedVoucherNo ?? ""}
                onChange={(e) => handleManualVoucherChange(e.target.value)}
                placeholder="Enter voucher no"
                error={
                  voucherCheck.taken || (voucherWarn && !manualVoucherFilled)
                }
                helperText={
                  voucherCheck.taken
                    ? "Already used — choose another"
                    : voucherCheck.checking
                    ? "Checking…"
                    : manualVoucherFilled
                    ? "Editable — enter any voucher no"
                    : "Manual voucher mode — enter a voucher number"
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        variant="caption"
                        sx={{ color: INK_MUTED, fontFamily: "monospace" }}
                      >
                        Voucher&nbsp;#
                      </Typography>
                    </InputAdornment>
                  ),
                  endAdornment: voucherCheck.checking ? (
                    <InputAdornment position="end">
                      <CircularProgress size={12} sx={{ color: BLUE }} />
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  mt: 0.2,
                  minWidth: 190,
                  "& .MuiInput-input": {
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                    py: 0.2,
                  },
                  "& .MuiFormHelperText-root": {
                    m: 0,
                    fontSize: "0.62rem",
                    lineHeight: 1.2,
                    color:
                      voucherCheck.taken ||
                      (voucherWarn && !manualVoucherFilled)
                        ? ERROR_C
                        : INK_MUTED,
                  },
                }}
              />
            ) : (
              <Typography
                variant="caption"
                sx={{ color: INK_MUTED, fontFamily: "monospace" }}
              >
                Voucher #{currentFormattedVoucherNo ?? currentVoucherNo ?? "—"}
              </Typography>
            )}
          </Box>
        </Box>

        {/* ── Ticket-number lookup: load an existing payment to edit.
                Centered on desktop, full-width row on mobile (below title). ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            flex: { lg: 1 },
            minWidth: 0,
            width: { xs: "100%", lg: "auto" },
          }}
        >
          {/* Nav: one ticket backward — the most recent one, from a fresh form */}
          <Tooltip title="Previous ticket">
            <span>
              <IconButton
                size="small"
                onClick={() => navigateTicket("prev")}
                disabled={loadingTicket}
                sx={navBtnSx}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            size="small"
            placeholder="Pawn ticket no / mobile no"
            value={ticketSearch}
            // Same box, two jobs: a ticket number typed and submitted with
            // Enter, or a mobile number that quietly drops a customer list
            // under the box (see customerTxnLookup).
            ref={customerLookup?.setAnchor}
            onChange={(e) => {
              setTicketSearch(e.target.value);
              customerLookup?.onSearchInput(e.target.value);
            }}
            onKeyDown={(e) => {
              // The lookup consumes Enter only on a complete 10-digit mobile
              // number (and Escape while its dropdown is open); everything
              // else falls through to the ticket lookup below.
              if (customerLookup?.handleKeyDown(e)) return;
              if (e.key === "Enter") {
                e.preventDefault();
                loadPawnForEdit(ticketSearch);
              }
            }}
            disabled={loadingTicket}
            inputProps={{ "data-skip-enter-tab": "true" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {loadingTicket || customerLookup?.resolving ? (
                    <CircularProgress size={16} sx={{ color: BLUE }} />
                  ) : (
                    <SearchIcon sx={{ fontSize: 18, color: INK_MUTED }} />
                  )}
                </InputAdornment>
              ),
              endAdornment: ticketSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setTicketSearch("");
                      customerLookup?.reset();
                    }}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              ...textFieldSx,
              flex: { xs: 1, lg: "initial" },
              minWidth: 0,
              width: { xs: "auto", lg: 240 },
            }}
          />
          {customerLookup && (
            <CustomerTxnPopper {...customerLookup.popperProps} />
          )}
          {/* Nav: one ticket forward. Only ever shown with a ticket loaded;
              stepping forward off the newest one clears back to a new entry,
              which is what makes this button disappear again. */}
          {canGoForward && (
            <Tooltip title="Next ticket">
              <span>
                <IconButton
                  size="small"
                  onClick={() => navigateTicket("next")}
                  disabled={loadingTicket}
                  sx={navBtnSx}
                >
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "stretch", lg: "flex-end" },
            gap: 1,
            rowGap: 1,
            flexWrap: "wrap",
            flexShrink: 0,
            width: { xs: "100%", lg: "auto" },
          }}
        >
          {/* Loan Date — editable so a payment can be back-dated;
                  defaults to today for a new loan, or the original
                  transaction date when editing an existing one */}
          <TextField
            size="small"
            label="Loan Date"
            type="date"
            value={formData.date}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                date: e.target.value || dayjs().format("YYYY-MM-DD"),
              }))
            }
            disabled={paymentLocked}
            InputLabelProps={{ shrink: true }}
            sx={{ ...textFieldSx, width: { xs: "100%", sm: 165 } }}
          />
          {!roleLocked && (
            <Tooltip
              title={
                paymentLocked
                  ? "Editing disabled — receipt(s) raised against this payment"
                  : "Save payment (Ctrl+Enter)"
              }
            >
              <Box
                component="span"
                sx={{ flex: { xs: 1, lg: "none" }, display: "inline-flex" }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  startIcon={loading ? null : <SaveIcon />}
                  onClick={handleSave}
                  disabled={loading || paymentLocked || voucherBlocked}
                  sx={{
                    bgcolor: BLUE,
                    "&:hover": { bgcolor: BLUE_DARK },
                    boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : paymentLocked ? (
                    "Locked"
                  ) : editMode ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </Button>
              </Box>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            sx={{
              flex: { xs: 1, lg: "none" },
              borderColor: BORDER_MED,
              color: INK_MUTED,
              "&:hover": {
                borderColor: ERROR_C,
                color: ERROR_C,
                bgcolor: ERROR_BG,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleNewPayment}
            sx={{
              flex: { xs: 1, lg: "none" },
              bgcolor: INK,
              "&:hover": { bgcolor: INK_SOFT },
              boxShadow: "none",
            }}
          >
            New
          </Button>
        </Box>
      </Box>

      {/* Receipt-locked banner — the payment is loaded for viewing only. */}
      {paymentLocked && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            p: 1.5,
            bgcolor: ERROR_BG,
            border: `1px solid ${ERROR_C}55`,
            borderRadius: "10px",
            color: ERROR_C,
            fontWeight: 700,
            fontSize: "0.85rem",
          }}
        >
          <LockIcon sx={{ fontSize: 18 }} />
          {loanSettled
            ? "Read only mode. Already settled loan"
            : roleLocked && !receiptLocked
            ? "Read only mode. Only the Store Head or Store HOD can edit an existing payment."
            : "Read only mode. Can not edit payment because receipt(s) is made against it."}
        </Box>
      )}
    </>
  );
};

export default PageHeader;
